import { api, unwrap, toPaginated, cleanParams } from "../lib/axios";
import type { ApiEnvelope, ListParams, PaginatedResult } from "../types/api";
import type { LostItem, SupportQuery } from "../types/domain";

export interface StatusUpdateBody {
  status: string;
  comment: string;
  updatedBy: string;
}

export const supportApi = {
  async listQueries(
    params: ListParams & { status?: string; tripID?: string },
  ): Promise<PaginatedResult<SupportQuery>> {
    const res = await api.get<ApiEnvelope<unknown>>("/query", {
      params: cleanParams(params),
    });
    return toPaginated<SupportQuery>(res, "queries");
  },

  // Lost items reported on a specific trip (GET /lost-item/trip/:tripID → array).
  async lostItemsByTrip(tripID: string): Promise<LostItem[]> {
    const res = await api.get<ApiEnvelope<LostItem[] | { lostItems: LostItem[] }>>(
      `/lost-item/trip/${tripID}`,
    );
    const data = unwrap(res);
    return Array.isArray(data) ? data : (data?.lostItems ?? []);
  },

  async listLostItems(
    params: ListParams & { status?: string },
  ): Promise<PaginatedResult<LostItem>> {
    const res = await api.get<ApiEnvelope<unknown>>("/lost-item", {
      params: cleanParams(params),
    });
    return toPaginated<LostItem>(res, "lostItems");
  },

  async updateQueryStatus(
    id: string,
    body: StatusUpdateBody,
  ): Promise<SupportQuery> {
    const res = await api.patch<ApiEnvelope<SupportQuery>>(
      `/query/${id}/status`,
      body,
    );
    return unwrap(res);
  },

  async updateLostItemStatus(
    id: string,
    body: StatusUpdateBody,
  ): Promise<LostItem> {
    const res = await api.patch<ApiEnvelope<LostItem>>(
      `/lost-item/${id}/status`,
      body,
    );
    return unwrap(res);
  },

  async createQuery(body: {
    queryType: string;
    message: string;
    createdByInfo: { userType: string; createdBy: string };
    tripID?: string;
    images?: string[];
  }): Promise<SupportQuery> {
    const res = await api.post<ApiEnvelope<SupportQuery>>("/query", body);
    return unwrap(res);
  },

  async createLostItem(body: {
    itemType: string;
    message: string;
    tripID: string;
    userID: string;
    driverID: string;
    images?: string[];
  }): Promise<LostItem> {
    const res = await api.post<ApiEnvelope<LostItem>>("/lost-item", body);
    return unwrap(res);
  },
};
